import {PolymerElement, html} from '@polymer/polymer';
import {ScopeBinding} from './behaviors/keli-binding-behavior.js';
import {ResizeObserverBehavior} from './behaviors/resize-observer-behavior.js';
import SignaturePad from 'signature_pad';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status';

class KeliSignature extends ResizeObserverBehavior(ScopeBinding(PolymerElement)) {
  static get template() {
    return html`
<style>
  :host {
    display: block;
    box-sizing: border-box;
    cursor: pointer;
    text-align: center;
    border: 2px solid var(--keli-subtle-border-color);
    background-color: var(--keli-subtle-background);
  }
  
  #signature {
    display: block;
    width: 100%;
    height: 150px;
  }
  
  #instructions {
    border-top: 2px solid var(--keli-subtle-border-color);
    color: var(--keli-text);
    text-align: center;
    padding: 10px 0 5px 0;
  }
</style>

<canvas id="signature"></canvas>
<div id="instructions">[[finalInstructions]]</div>`;
  }

  static get is() {
    return 'keli-signature';
  }

  static get properties() {
    return {
      _initialized: {type: Boolean},
      keliControlTypes: {type: Array, value: ['signature']},
      signatureInstance: {type: Object},
      instructions: {type: String, scopevar: true},
      finalInstructions: {type: String, computed: 'computeInstructions(instructions, _initialized)'}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli(['signatureDataSaved']);
    this.addOnKeli({
      'saveSignature': 'handleSaveSignature',
      'clearSignature': 'handleClearSignature'
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.signatureInstance = new SignaturePad(this.$.signature, {
      minDistance: 1,
      throttle: 0,
      penColor: (KeliInterface.Styles.palette.darkMode ? 'white' : 'black')
    });

    this._initialized = true;

    afterNextRender(this.$.signature, () => {
      this.scaleCanvasToDevice();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.signatureInstance = null;
  }

  computeInstructions(instructions) {
    return instructions || 'Please sign in the space above.';
  }

  scaleCanvasToDevice() {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    this.$.signature.width = this.$.signature.offsetWidth * ratio;
    this.$.signature.height = this.$.signature.offsetHeight * ratio;
    this.$.signature.getContext('2d').scale(ratio, ratio);
  }

  handleElementResized() {
    this.scaleCanvasToDevice();
    this.signatureInstance && this.signatureInstance.clear();
  }

  handleSaveSignature() {
    let points;
    try {
      points = this.toDataPoints();
    } catch (error) {
      keliUILogger(error)
    }
    this.dispatchEvent(new CustomEvent('signatureDataSaved', {detail: {signature: points}}));
  }

  handleClearSignature() {
    this.signatureInstance && this.signatureInstance.clear();
  }

  toDataPoints() {
    if (!this.signatureInstance) {
      return;
    }
    const finalData = {
      points: [],
      count: 0,
      box: {
        width: this.$.signature.width,
        height: this.$.signature.height
      }
    };

    const minimumPressure = 70;
    const pressureRange = 300;
    const pressureMultiplier = 1.5;
    const smoothness = 1.01;

    const box = {
      left: Number.MAX_SAFE_INTEGER,
      right: 0,
      top: Number.MAX_SAFE_INTEGER,
      bottom: 0
    };

    const lines = this.signatureInstance.toData();
    for (const line of lines) {
      let isStart = true;

      let firstPointReference;
      let lastPoint;
      let lastPressure;
      for (const point of line.points) {
        let velocity = 100;
        if (lastPoint) {
          const a = Math.abs(point.x - lastPoint.x);
          const b = Math.abs(point.y - lastPoint.y);
          const c = Math.sqrt((a * a) + (b * b));
          const time = point.time - lastPoint.time;
          velocity = time * c;
        }

        let pressure = minimumPressure + (pressureRange / (velocity / pressureMultiplier));
        if (lastPressure) {
          pressure = Math.min(lastPressure * smoothness, pressure);
          pressure = Math.max(lastPressure / smoothness, pressure);
        }

        if (firstPointReference) {
          firstPointReference.p = pressure;
          firstPointReference = null;
        }

        lastPoint = point;
        lastPressure = pressure;

        finalData.points.push({
          x: point.x,
          y: point.y,
          p: pressure,
          d: (isStart ? 1 : 0),
          ts: point.time
        });

        // Set the box to the signature bounds
        if (point.x < box.left) box.left = point.x;
        if (point.x > box.right) box.right = point.x;
        if (point.y < box.top) box.top = point.y;
        if (point.y > box.bottom) box.bottom = point.y;

        if (isStart) {
          firstPointReference = finalData.points[finalData.points.length - 1];
          isStart = false;
        }
      }
    }

    // Adjust all points to the upper-left corner
    for (const point of finalData.points) {
      point.x -= (box.left - 1);
      point.y -= (box.top - 1);
    }

    finalData.count = finalData.points.length;
    finalData.box = {
      width: box.right - box.left,
      height: box.bottom - box.top
    };

    if (finalData.count) {
      return finalData;
    }
  }

  fromDataPoints(data) {
    if (!this.signatureInstance) {
      return;
    }
    if (!data || !data.box || !data.points || !data.points.length) {
      this.signatureInstance.clear();
      return;
    }

    const widthFactor = this.$.signature.width / data.box.width;
    const heightFactor = this.$.signature.height / data.box.height;

    const lines = [];
    let currentLine = {color: 'black', points: []};
    for (const point of data.points) {
      if (point.d === 1 && currentLine.points.length) {
        lines.push(currentLine);
        currentLine = {color: 'black', points: []};
      }

      currentLine.points.push({
        x: point.x * widthFactor,
        y: point.y * heightFactor,
        time: point.ts,
      });
    }
    if (currentLine.points.length) {
      lines.push(currentLine);
    }

    this.signatureInstance.fromData(lines);
  }
}

customElements.define(KeliSignature.is, KeliSignature);
export {KeliSignature};
